import { createSelector } from '@reduxjs/toolkit';
import { get, isNil, pipe, omit, values, filter, size, map, identity } from 'lodash/fp';
import { toStringFqn } from 'app/lib/utils';
import {
    selectDatasetColumnWidthsForEntityId,
    selectExplorerColumnWidthsForEntityId
} from 'app/store/columnWidths/selectors';
import {
    selectDatasetColumnFilterForEntityId,
    selectDatasetColumnFilterForPropFqn,
    selectExploreColumnFilterForQueryUuid
} from 'app/store/columnFilter/selectors';
import {
    applyColumnFilter,
    applyCustomColumnWidths,
    applyEntityId,
    transformDataset
} from 'app/store/transformers.js';
import { selectDatasizeInfoByPropId } from 'app/store/access/selectors';
import { QueryState } from 'app/lib/constants';
import { extractRowCountFromLastColumn, mergeQuery, rowsToColumns } from './utils.js';

export const selectSlice = get('snowflakeQueries');
const selectPropEntityId = (_, props) => props.entityId;
const selectPropQueryKey = (_, props) => props.queryKey;
const selectPropQueryUuid = (_, props) => props.queryUuid;
const selectPropFQN = (_, props) => props.fqn;
const selectPropQueryId = (_, props) => props.queryId;

export const selectIsCancelingAllQueries = createSelector(
    selectSlice,
    get('isCancelingAllQueries')
);

export const selectQueries = createSelector(selectSlice, get('byQueryId'));

const selectDatasetMetadatas = createSelector(selectSlice, get('byEntityId'));

const selectDatasetMetadata = createSelector([selectPropEntityId, selectDatasetMetadatas], get);

const selectRowCountMetadatas = createSelector(
    selectSlice,
    get(['metrics', 'rowCount', 'byFqnOrId'])
);

export const selectQueryForPropEntityId = createSelector(
    [selectQueries, selectDatasetMetadata],
    mergeQuery
);

export const selectDatasetQueries = createSelector(
    [selectQueries, selectDatasetMetadatas],
    (queries, metadatas) =>
        Object.keys(metadatas).reduce((acc, entityId) => {
            acc[entityId] = mergeQuery(queries, metadatas[entityId]);

            return acc;
        }, {})
);

const selectFQNPreviewMetadatas = createSelector(selectSlice, get('byFQN'));

const selectFQNPreviewMetadata = createSelector(
    selectPropFQN,
    selectFQNPreviewMetadatas,
    (fqn, metadatas) => metadatas[toStringFqn(fqn)]
);

const selectQueryForPropFQN = createSelector(selectQueries, selectFQNPreviewMetadata, mergeQuery);

const selectMetadataForPropKey = createSelector([selectPropQueryKey, selectSlice], get);

const selectExploreQueryMetadatas = createSelector(selectSlice, get('byExploreQueryUuid'));

export const selectExploreQueries = createSelector(
    selectQueries,
    selectExploreQueryMetadatas,
    (queries, metadatas) =>
        Object.keys(metadatas).reduce((acc, queryUuid) => {
            acc[queryUuid] = mergeQuery(queries, metadatas[queryUuid]);

            return acc;
        }, {})
);

const selectExploreQueryMetadata = createSelector(
    selectPropQueryUuid,
    selectExploreQueryMetadatas,
    get
);

export const selectQueryForPropQueryUuid = createSelector(
    selectQueries,
    selectExploreQueryMetadata,
    mergeQuery
);

export const selectQueryForPropKey = createSelector(
    selectQueries,
    selectMetadataForPropKey,
    mergeQuery
);

export const selectQueryMetadataForPropKey = createSelector(selectQueryForPropKey, (query) =>
    omit(['data'], query)
);

export const selectQueryStateForPropKey = createSelector(
    selectQueryMetadataForPropKey,
    (metadata) => metadata?.state
);

export const selectQueryResultsForPropKey = createSelector([selectQueryForPropKey], (query) =>
    isNil(query?.data)
        ? null
        : pipe(rowsToColumns, (d) => transformDataset(d, query.entityId))(query.data)
);

export const selectQueryResultsWithRowCountForPropKey = createSelector(
    selectQueryForPropKey,
    (query) =>
        isNil(query?.data)
            ? null
            : pipe(extractRowCountFromLastColumn, rowsToColumns, (d) =>
                  transformDataset(d, query.entityId)
              )(query.data)
);

export const selectQueryResultsForPropEntityId = createSelector(
    selectQueryForPropEntityId,
    selectDatasetColumnWidthsForEntityId,
    selectDatasetColumnFilterForEntityId,
    selectPropEntityId,
    (query, updatedColumnWidths, columnFilter, entityId) => {
        if (isNil(query?.data)) {
            return null;
        }

        return pipe(
            rowsToColumns,
            (d) => transformDataset(d, query.entityId),
            applyCustomColumnWidths(updatedColumnWidths),
            applyColumnFilter(columnFilter),
            applyEntityId(entityId)
        )(query.data);
    }
);

export const selectQueryMetadataForPropEntityId = createSelector(
    selectQueryForPropEntityId,
    (query) => omit(['data'], query)
);

export const selectQueryResultsForPropFQN = createSelector(
    selectQueryForPropFQN,
    selectDatasetColumnFilterForPropFqn,
    (query, columnFilter) => {
        if (isNil(query?.data)) {
            return null;
        }

        return pipe(
            rowsToColumns,
            (d) => transformDataset(d, query.entityId),
            applyColumnFilter(columnFilter)
        )(query.data);
    }
);

export const selectQueryMetadataForPropFQN = createSelector(selectQueryForPropFQN, (query) =>
    omit(['data'], query)
);

export const selectQueryStateForPropFQN = createSelector(
    selectQueryMetadataForPropFQN,
    (metadata) => metadata?.state
);

export const selectQuerySchemaForPropFQN = createSelector(
    selectQueryForPropFQN,
    get('data.columns')
);

export const selectQueryResultsForPropQueryUuid = createSelector(
    selectQueryForPropQueryUuid,
    selectExplorerColumnWidthsForEntityId,
    selectExploreColumnFilterForQueryUuid,
    (query, updatedColumnWidths, columnFilter) => {
        if (isNil(query?.data)) {
            return null;
        }

        return pipe(
            query.meta?.skipRowCount ? identity : extractRowCountFromLastColumn,
            rowsToColumns,
            (d) => transformDataset(d, query.entityId),
            applyCustomColumnWidths(updatedColumnWidths?.[query.queryUuid]),
            applyColumnFilter(columnFilter)
        )(query.data);
    }
);

export const selectQueryMetadataForPropQueryUuid = createSelector(
    selectQueryForPropQueryUuid,
    (query) => omit(['data'], query)
);

const selectQueryForRowCount = createSelector(
    [selectPropFQN, selectPropEntityId, selectRowCountMetadatas, selectQueries],
    (fqn, entityId, rowCountMetadatas, queries) => {
        const metadata = rowCountMetadatas[fqn ?? entityId];

        return mergeQuery(queries, metadata);
    }
);

export const selectQueryMetadataForRowCountQuery = createSelector(
    [selectQueryForRowCount],
    (query) => omit(['data'], query)
);

export const selectQueryResultsForRowCountQuery = createSelector(
    [selectQueryForRowCount],
    (query) => query?.data?.records[0][0]
);

export const selectDatasizeInfoForPropEntityId = createSelector(
    [selectQueryForPropEntityId, selectQueryResultsForRowCountQuery, selectDatasizeInfoByPropId],
    (queryResults, rowCount, datasizeInfo) => {
        if (!queryResults) {
            return null;
        }

        return {
            sampledInputTables: queryResults.sampledInputTables ?? [],
            recordCount: rowCount ?? datasizeInfo?.recordCount,
            sampleSize: datasizeInfo?.sampleSize
        };
    }
);

export const selectRunningQueriesMetadatasForPropKey = createSelector(
    selectMetadataForPropKey,
    (metadatas) =>
        Object.values(metadatas ?? {}).filter((metadata) => metadata?.state === QueryState.RUNNING)
);

export const selectQueryCount = createSelector(selectQueries, pipe(values, size));

export const selectExecutingQueryCount = createSelector(
    selectQueries,
    pipe(values, filter({ state: QueryState.RUNNING }), size)
);

export const selectQueuedQueryCount = createSelector(
    selectQueries,
    pipe(values, filter({ isQueued: true }), size)
);

export const selectIsExecuting = createSelector(selectExecutingQueryCount, (n) => n > 0);

export const selectQueriesWithMetadata = createSelector(selectSlice, (slice) =>
    pipe(
        get('byQueryId'),
        values,
        map((query) => ({
            ...get(query.queryKey, slice),
            ...query
        }))
    )(slice)
);

export const selectQueryForPropQueryId = createSelector(
    selectPropQueryId,
    selectSlice,
    (queryId, slice) => {
        const query = slice.byQueryId[queryId];
        const metadata = get(query.queryKey, slice);

        return {
            ...metadata,
            ...query
        };
    }
);

export const selectExploreQueriesForPropEntityId = createSelector(
    selectPropEntityId,
    selectExploreQueryMetadatas,
    (entityId, queries) =>
        Object.values(queries ?? {}).filter((query) => query.meta.entityId === entityId)
);
